import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ClockAlert, Check, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function fmtTimeOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const now = Date.now();
    const min = Math.floor((now - t) / 60000);
    if (min < 60) return `há ${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h${min % 60 ? ` ${min % 60}min` : ""}`;
    const d = Math.floor(h / 24);
    return `há ${d}d`;
  } catch {
    return "—";
  }
}

export default function AdminPendencias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [form, setForm] = useState({ checkout_at: "", reason: "", manager_name: "" });

  // Diagnóstico/reparo emergencial de índices
  const [indexStatus, setIndexStatus] = useState(null);
  const [repairing, setRepairing] = useState(false);

  const checkIndexStatus = () => {
    api.get("/admin/maintenance/attendance-indexes")
      .then(r => setIndexStatus(r.data))
      .catch(() => setIndexStatus(null));
  };

  const repairIndexes = async () => {
    if (!window.confirm("Confirmar reparo de índices do banco?\n\nIsso vai remover qualquer índice ÚNICO da coleção 'attendance' que esteja bloqueando múltiplos check-ins no mesmo dia. Operação SEGURA.")) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/maintenance/repair-attendance-indexes");
      toast.success(`Reparo concluído. Índices únicos removidos: ${data?.report?.dropped?.length || 0}`);
      checkIndexStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reparar índices");
    } finally {
      setRepairing(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/attendance/pendencies")
      .then(r => setItems(r.data || []))
      .catch(() => toast.error("Erro ao carregar pendências"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); checkIndexStatus(); }, []);

  const openResolve = (att) => {
    const ci = att.checkin_at ? new Date(att.checkin_at) : new Date();
    // Sugere checkout = checkin + 4h por padrão (período típico de manhã ou tarde)
    const suggested = new Date(ci.getTime() + 4 * 60 * 60 * 1000);
    const isoLocal = (d) => {
      const tz = d.getTimezoneOffset() * 60000;
      return new Date(d - tz).toISOString().slice(0, 16);
    };
    setResolving(att);
    setForm({
      checkout_at: isoLocal(suggested),
      reason: "",
      manager_name: user?.full_name || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resolving) return;
    if (!form.reason.trim() || form.reason.trim().length < 5) {
      toast.error("Informe a justificativa (mínimo 5 caracteres)");
      return;
    }
    if (!form.manager_name.trim()) {
      toast.error("Informe o nome do gestor responsável");
      return;
    }
    try {
      await api.patch(`/admin/attendance/${resolving.id}`, {
        checkout_at: new Date(form.checkout_at).toISOString(),
        reason: form.reason.trim(),
        manager_name: form.manager_name.trim(),
      });
      toast.success("Pendência resolvida — checkout registrado.");
      setResolving(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao resolver");
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remover a sessão pendente de ${att.user?.full_name}?\n\nUse esta opção apenas se o aluno foi marcado por engano.`)) return;
    try {
      await api.delete(`/admin/attendance/${att.id}`);
      toast.success("Sessão removida.");
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  return (
    <AdminLayout title="Pendências de Presença">
      {/* Diagnóstico do banco — só aparece quando há índice problemático */}
      {indexStatus?.has_unique_legacy && (
        <Card className="bg-rose-50 border-rose-300 mb-4" data-testid="index-repair-card">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-rose-900">
              <p className="font-medium">🚨 Problema crítico detectado no banco de dados</p>
              <p className="text-xs mt-1 opacity-90">
                A coleção <code className="bg-white/60 px-1 rounded">attendance</code> ainda possui um <strong>índice único legado</strong> que está bloqueando múltiplos check-ins por dia. Isso impede que alunos façam check-in no período da tarde após o da manhã. Clique no botão para corrigir imediatamente.
              </p>
              <Button
                onClick={repairIndexes}
                disabled={repairing}
                className="mt-3 bg-rose-600 hover:bg-rose-700 text-white"
                size="sm"
                data-testid="repair-indexes-btn"
              >
                <Wrench className="w-4 h-4 mr-1.5" />
                {repairing ? "Corrigindo…" : "Corrigir índices agora"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-amber-50 border-amber-200 mb-4">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">Sessões abertas há mais de 3 horas sem checkout</p>
            <p className="text-xs mt-1 opacity-90">
              Geralmente significa que o aluno esqueceu de fazer o check-out (saiu para almoçar, por exemplo).
              Resolva manualmente preenchendo o horário aproximado de saída.
              <strong> Sem resolver as pendências, o certificado pode não ser emitido automaticamente.</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClockAlert className="w-4 h-4 text-amber-600" />
            {loading ? "Carregando…" : `${items.length} pendência${items.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aluno</TableHead>
                <TableHead>Curso</TableHead>
                <TableHead>Dia</TableHead>
                <TableHead>Check-in</TableHead>
                <TableHead>Tempo aberto</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(p => (
                <TableRow key={p.id} data-testid={`pendency-${p.id}`}>
                  <TableCell>
                    <div className="font-medium">{p.user?.full_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{p.user?.ceap_id}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{p.course?.title || "—"}</div>
                    {p.course?.cidade && (
                      <div className="text-[11px] text-muted-foreground">{p.course.cidade}/{p.course.estado}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{p.attendance_date}</TableCell>
                  <TableCell className="text-xs">{fmtTimeOnly(p.checkin_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">
                      {timeAgo(p.checkin_at)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="default" onClick={() => openResolve(p)} data-testid={`resolve-${p.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Resolver
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-rose-600" data-testid={`remove-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-sm text-muted-foreground">
                    ✅ Nenhuma pendência. Todas as sessões estão fechadas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de resolução */}
      <Dialog open={!!resolving} onOpenChange={(v) => !v && setResolving(null)}>
        <DialogContent className="max-w-[520px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Resolver pendência de presença</DialogTitle>
            <DialogDescription>
              Informe o horário aproximado em que o aluno saiu do curso e a justificativa da correção.
            </DialogDescription>
          </DialogHeader>
          {resolving && (
            <form onSubmit={submit} className="space-y-3">
              <Card className="bg-[hsl(var(--secondary))] border-0">
                <CardContent className="p-3 text-sm space-y-1">
                  <p><strong>Aluno:</strong> {resolving.user?.full_name}</p>
                  <p><strong>Curso:</strong> {resolving.course?.title}</p>
                  <p><strong>Check-in registrado:</strong> {fmtDateTime(resolving.checkin_at)}</p>
                </CardContent>
              </Card>
              <div>
                <Label>Hora aproximada da saída (check-out) *</Label>
                <Input
                  type="datetime-local"
                  required
                  value={form.checkout_at}
                  onChange={(e) => setForm(f => ({ ...f, checkout_at: e.target.value }))}
                  data-testid="resolve-checkout-at"
                />
              </div>
              <div>
                <Label>Justificativa *</Label>
                <Textarea
                  rows={2}
                  required
                  minLength={5}
                  value={form.reason}
                  onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Ex: Aluno esqueceu de bater check-out antes do almoço. Confirmei presença até as 12h."
                  data-testid="resolve-reason"
                />
              </div>
              <div>
                <Label>Nome do gestor responsável *</Label>
                <Input
                  required
                  value={form.manager_name}
                  onChange={(e) => setForm(f => ({ ...f, manager_name: e.target.value }))}
                  placeholder="Seu nome completo"
                  data-testid="resolve-manager"
                />
              </div>
              <p className="text-[11px] text-muted-foreground italic">
                A correção será registrada na auditoria do sistema com sua identificação.
              </p>
              <Button type="submit" className="w-full" data-testid="resolve-submit">
                <Check className="w-4 h-4 mr-1" /> Confirmar resolução
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
